import { useEffect, useState, type HTMLAttributes } from 'react'
import { cn } from '../lib/cn'

/**
 * Carregando: esqueleto com a forma das linhas, nunca spinner central. Ação carrega no próprio botão.
 * A frase de espera (`WaitPhrase`) só aparece se a espera passar de 800ms.
 */

export function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="skeleton"
      aria-hidden
      className={cn('bg-alto/60 animate-pulse', className)}
      {...props}
    />
  )
}

export interface TableSkeletonProps {
  rows?: number
  columns?: number
  className?: string
}

/** Esqueleto da própria tabela: cabeçalho e linhas com a mesma altura da linha real. */
export function TableSkeleton({ rows = 5, columns = 4, className }: TableSkeletonProps) {
  return (
    <div
      data-slot="table-skeleton"
      role="status"
      aria-label="Carregando"
      className={cn('border-hairline bg-surface border', className)}
    >
      <div className="border-hairline bg-pampas flex gap-3 border-b px-4 py-3">
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} className="h-[11px] flex-1" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div
          key={r}
          className="border-hairline/60 flex items-center gap-3 border-b px-4 py-[14px] last:border-b-0"
        >
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} className={cn('h-[13px] flex-1', c === 0 && 'h-[15px]')} />
          ))}
        </div>
      ))}
    </div>
  )
}

/** Verdadeiro só depois de `delay` ms — para não piscar esqueleto em cargas rápidas. */
export function useDelayed(active: boolean, delay = 800) {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    if (!active) return
    const t = setTimeout(() => setShown(true), delay)
    return () => {
      clearTimeout(t)
      setShown(false)
    }
  }, [active, delay])
  return active && shown
}

export type WaitMoment =
  | 'generating'
  | 'sending'
  | 'signing'
  | 'resending'
  | 'saving'
  | 'loading'
  | 'exporting'
  | 'signing_in'
  | 'long'

/**
 * Frases por momento (docs/design 02 · seção 06c). Sempre no gerúndio e na primeira pessoa do plural.
 * Nunca "aguarde", "processando", porcentagem falsa ou exclamação.
 */
export const WAIT_PHRASES: Record<WaitMoment, readonly string[]> = {
  generating: [
    'Montando seus documentos com o cuidado que eles exigem.',
    'Escrevendo cada cláusula com os dados do procedimento.',
    'Preparando a documentação do atendimento.',
  ],
  sending: [
    'Levando os documentos até o seu paciente.',
    'Enviando por WhatsApp. Em instantes ele recebe o link.',
    'Seu paciente está a um toque de assinar.',
  ],
  signing: [
    'Sua assinatura está sendo registrada.',
    'Guardando data, hora e o documento exato que você leu.',
    'Só um instante: estamos deixando tudo comprovado.',
  ],
  resending: [
    'Reenviando as pendências. Nenhum documento novo é criado.',
    'Retomando de onde cada paciente parou.',
  ],
  saving: [
    'Guardando as informações do paciente.',
    'Salvando. Você pode continuar em outro momento.',
  ],
  loading: ['Reunindo seus pacientes.', 'Organizando o histórico da clínica.'],
  exporting: ['Preparando o arquivo para você levar.', 'Reunindo os PDFs em um único download.'],
  signing_in: ['Abrindo a sua clínica.', 'Bem-vinda de volta. Preparando seu painel.'],
  long: [
    'Está levando mais tempo que o normal. Não feche a página — nada foi perdido.',
    'Continuamos trabalhando. Se preferir, avisamos por e-mail quando terminar.',
  ],
}

export interface WaitPhraseProps extends HTMLAttributes<HTMLParagraphElement> {
  moment: WaitMoment
  /** Ativa a espera; a frase só aparece após `delay` (800ms) e troca para a de `long` após `longAfter` (8s). */
  active?: boolean
  delay?: number
  longAfter?: number
  /** Alinhamento: centralizado quando ocupa a tela inteira, à esquerda quando é bloco. */
  align?: 'center' | 'left'
}

function pick(list: readonly string[]) {
  return list[Math.floor(Math.random() * list.length)] ?? list[0] ?? ''
}

export function WaitPhrase({
  moment,
  active = true,
  delay = 800,
  longAfter = 8000,
  align = 'left',
  className,
  ...props
}: WaitPhraseProps) {
  const shown = useDelayed(active, delay)
  const isLong = useDelayed(active, longAfter)
  const [phrase] = useState(() => pick(WAIT_PHRASES[moment]))
  const [longPhrase] = useState(() => pick(WAIT_PHRASES.long))
  if (!shown) return null
  return (
    <p
      data-slot="wait-phrase"
      role="status"
      aria-live="polite"
      className={cn(
        'font-body text-name text-secondary leading-[1.6] font-light',
        align === 'center' && 'text-center',
        className,
      )}
      {...props}
    >
      {isLong ? longPhrase : phrase}
    </p>
  )
}
